import { ElementState } from '../renderer/ElementState';

/**
 * Represents a boundary point for element snapping
 */
export interface ElementBoundary {
  time: number;
  type: 'start' | 'end';
  elementId: string;
}

/**
 * Creates pre-calculated boundaries for efficient snapping during drag operations
 * @param elements - All timeline elements
 * @param activeElementIds - IDs of currently active/dragged elements to exclude from snapping
 * @param snapThreshold - Maximum distance in seconds to snap (default: 0.1s)
 * @returns Pre-calculated boundaries sorted by time
 */
export function createSnapBoundaries(
  elements: ElementState[],
  activeElementIds: string[] = [],
): ElementBoundary[] {
  if (!elements || elements.length === 0) {
    return [];
  }

  // Filter out active elements (the ones being dragged)
  const otherElements = elements.filter(
    element => !activeElementIds.includes(element.source.id),
  );

  if (otherElements.length === 0) {
    return [];
  }

  // Collect all element boundaries with metadata
  const boundaries: ElementBoundary[] = [];

  for (const element of otherElements) {
    // Add element start time
    boundaries.push({
      time: element.globalTime,
      type: 'start',
      elementId: element.source.id,
    });

    // Add element end time
    boundaries.push({
      time: element.globalTime + element.duration,
      type: 'end',
      elementId: element.source.id,
    });
  }

  // Sort boundaries by time for efficient binary search
  boundaries.sort((a, b) => a.time - b.time);

  return boundaries;
}

function findClosestBoundary(
  time: number,
  boundaries: ElementBoundary[],
): ElementBoundary | null {
  if (boundaries.length === 0) {
    return null;
  }

  // Binary search to find insertion point
  let left = 0;
  let right = boundaries.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (boundaries[mid].time < time) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Check boundaries around insertion point for closest match within threshold
  const candidates: ElementBoundary[] = [];

  // Check boundary to the left
  if (left > 0) {
    const leftBoundary = boundaries[left - 1];
    candidates.push(leftBoundary);
  }

  // Check boundary to the right
  if (left < boundaries.length) {
    const rightBoundary = boundaries[left];
    candidates.push(rightBoundary);
  }

  if (candidates.length === 0) {
    return null;
  }

  // Return the closest candidate
  return candidates.reduce((closest, boundary) => {
    const distance = Math.abs(time - boundary.time);
    const closestDistance = Math.abs(time - closest.time);
    return distance < closestDistance ? boundary : closest;
  });
}

/**
 * Efficiently finds the closest boundary using binary search
 * @param time - The time to find closest boundary for
 * @param boundaries - Sorted array of boundaries
 * @param snapThreshold - Maximum distance to consider for snapping
 * @returns Closest boundary within threshold, or null if none found
 */
function findClosestBoundaryWithinSnapThreshold(
  time: number,
  boundaries: ElementBoundary[],
  snapThreshold: number,
): ElementBoundary | null {
  if (boundaries.length === 0) {
    return null;
  }

  const closestBoundary = findClosestBoundary(time, boundaries);

  if (!closestBoundary) {
    return null;
  }

  if (Math.abs(time - closestBoundary.time) <= snapThreshold) {
    return closestBoundary;
  }

  return null;
}

/**
 * Snaps time to the closest boundary using pre-calculated boundaries
 * @param time - The time to snap
 * @param snapBoundaries - Pre-calculated boundaries
 * @returns The snapped time, or original time if no nearby boundaries found
 */
export function snapTimeToBoundaries(
  time: number,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): number {
  const closestBoundary = findClosestBoundaryWithinSnapThreshold(
    time,
    snapBoundaries,
    snapThreshold,
  );

  return closestBoundary ? closestBoundary.time : time;
}

export interface SnapResult {
  snapTime: number | undefined;
  snapGuideTime: number | undefined;
  isWithinSnapRange: boolean;
  isWithinSnapWarningRange: boolean;
}

export function getSnapResultForTime(
  time: number,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): SnapResult {
  const closestBoundary = findClosestBoundary(time, snapBoundaries);

  let snapTime;
  let isWithinSnapRange;
  let isWithinSnapWarningRange;

  if (!closestBoundary) {
    snapTime = undefined;
    isWithinSnapRange = false;
    isWithinSnapWarningRange = false;
  } else {
    isWithinSnapRange = Math.abs(time - closestBoundary.time) <= snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time - closestBoundary.time) <= snapThreshold * 5;
    snapTime = isWithinSnapWarningRange ? closestBoundary.time : undefined;
  }

  return {
    snapTime,
    snapGuideTime: snapTime,
    isWithinSnapRange,
    isWithinSnapWarningRange,
  };
}

export function getSnapResultForElement(
  time: number,
  element: ElementState,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): SnapResult {
  const closestHeadBoundary = findClosestBoundary(time, snapBoundaries);
  const closestTailBoundary = findClosestBoundary(
    time + element.duration,
    snapBoundaries,
  );

  const isHeadBoundaryCloser =
    closestHeadBoundary &&
    (!closestTailBoundary ||
      Math.abs(time - closestHeadBoundary.time) <=
        Math.abs(time + element.duration - closestTailBoundary.time));

  const isTailBoundaryCloser =
    closestTailBoundary &&
    (!closestHeadBoundary ||
      Math.abs(time + element.duration - closestTailBoundary.time) <=
        Math.abs(time - closestHeadBoundary.time));

  let snapTime: number | undefined;
  let snapGuideTime: number | undefined;
  let isWithinSnapRange;
  let isWithinSnapWarningRange;

  if (isHeadBoundaryCloser) {
    isWithinSnapRange =
      Math.abs(time - closestHeadBoundary.time) <= snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time - closestHeadBoundary.time) <= snapThreshold * 5;
    snapTime = isWithinSnapWarningRange ? closestHeadBoundary.time : undefined;
    snapGuideTime = isWithinSnapWarningRange
      ? closestHeadBoundary.time
      : undefined;
  } else if (isTailBoundaryCloser) {
    isWithinSnapRange =
      Math.abs(time + element.duration - closestTailBoundary.time) <=
      snapThreshold;
    isWithinSnapWarningRange =
      Math.abs(time + element.duration - closestTailBoundary.time) <=
      snapThreshold * 5;
    snapTime = isWithinSnapWarningRange
      ? closestTailBoundary.time - element.duration
      : undefined;
    snapGuideTime = isWithinSnapWarningRange
      ? closestTailBoundary.time
      : undefined;
  } else {
    snapTime = undefined;
    snapGuideTime = undefined;
    isWithinSnapRange = false;
    isWithinSnapWarningRange = false;
  }

  return {
    snapTime,
    snapGuideTime,
    isWithinSnapRange,
    isWithinSnapWarningRange,
  };
}

/**
 * Snaps element time to the closest boundary using pre-calculated boundaries.
 * This considers both the head and tail of the element, and their distances to the
 * nearest boundaries. If both the head and tail of the element are within the
 * snap threshold, the smaller gap is used to determine which boundary to snap to.
 * @param proposedElementStartTimecode - The desired new start time of the element.
 * @param element - The element to snap
 * @param snapBoundaries - Pre-calculated boundaries
 * @returns The snapped time, or original time if no nearby boundaries found
 */
export function snapElementTimeToBoundaries(
  proposedElementStartTimecode: number,
  element: ElementState,
  snapBoundaries: ElementBoundary[],
  snapThreshold: number,
): number {
  const closestHeadBoundary = findClosestBoundaryWithinSnapThreshold(
    proposedElementStartTimecode,
    snapBoundaries,
    snapThreshold,
  );

  const closestTailBoundary = findClosestBoundaryWithinSnapThreshold(
    proposedElementStartTimecode + element.duration,
    snapBoundaries,
    snapThreshold,
  );

  if (closestHeadBoundary && !closestTailBoundary) {
    return closestHeadBoundary.time;
  } else if (!closestHeadBoundary && closestTailBoundary) {
    return closestTailBoundary.time - element.duration;
  } else if (closestHeadBoundary && closestTailBoundary) {
    const gapAtElementHead = Math.abs(
      proposedElementStartTimecode - closestHeadBoundary.time,
    );
    const gapAtElementTail = Math.abs(
      closestTailBoundary.time -
        (proposedElementStartTimecode + element.duration),
    );
    if (gapAtElementHead < gapAtElementTail) {
      return closestHeadBoundary.time; // snap the head to its closest boundary
    }
    return closestTailBoundary.time - element.duration; // snap the tail to its closest boundary
  } else {
    return proposedElementStartTimecode;
  }
}

export function generateSnapThreshold(timelineScale: number = 50): number {
  if (timelineScale < 5) return 2;
  if (timelineScale < 10) return 1;
  return 10 / timelineScale;
}
